/**
 * Turn a stored document name into a title a visitor should read.
 *
 * Uploaded sources keep their raw file name in `documents.name`
 * ("03-HelpGenie-Conversation-Management-Guide.txt"), which is the right thing
 * for the owner's knowledge base and the wrong thing on a public page. This
 * derives the display title without ever touching the stored name.
 *
 * Deliberately cautious: a name that already reads as a title — it has spaces
 * and no file extension — is returned untouched, because "Returns — what you
 * need to know" must not be "improved".
 */

const FILE_EXTENSION =
  /\.(txt|md|markdown|pdf|docx?|rtf|html?|csv|tsv|xlsx?|pptx?|json|png|jpe?g|webp|mp4|mov)$/i;

/** Words kept lowercase mid-title when we do have to re-case a filename. */
const SMALL_WORDS = new Set([
  "a", "an", "and", "as", "at", "but", "by", "for", "in", "of",
  "on", "or", "the", "to", "vs", "with",
]);

export const prettifyGuideTitle = (name: string): string => {
  const raw = name.trim();
  if (!raw) return raw;

  const withoutExtension = raw.replace(FILE_EXTENSION, "").trim();
  const hadExtension = withoutExtension !== raw;
  const looksLikeFileName = hadExtension || !withoutExtension.includes(" ");

  // Already a human title — only ever strip a stray extension.
  if (!looksLikeFileName) return raw;
  if (withoutExtension.includes(" ")) return withoutExtension || raw;

  const spaced = withoutExtension
    // Separators used in file names become spaces.
    .replace(/[-_.]+/g, " ")
    // CamelCase boundaries: "HelpGenie" → "Help Genie", "FAQGuide" → "FAQ Guide".
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    // Leading list numbering: "03 Conversation…" → "Conversation…".
    .replace(/^\d+\s+/, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!spaced) return withoutExtension || raw;

  const words = spaced.split(" ").map((word, index) => {
    // Mixed-case and uppercase words (acronyms, product names) already chose
    // their casing — only fully-lowercase words get title-cased.
    if (!/^[a-z]/.test(word) || word !== word.toLowerCase()) return word;
    if (index > 0 && SMALL_WORDS.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  return words.join(" ");
};
